/**
 * Connect external source — Wave 2 finish.
 *
 * Multi-step wizard. Now wired to the real /api/v1/library/connectors
 * surface (no more mocks). Flow per kind:
 *
 *   onedrive / sharepoint / github:
 *     1. Choose provider
 *     2. Enter display name + (kind-specific) config
 *     3. Click "Authenticate" -> calls POST /:id/oauth-start, redirects
 *        the user to provider authUrl. After provider callback the
 *        connector lands authenticated; user comes back here and
 *        clicks "Done".
 *
 *   webhook:
 *     1. Choose provider
 *     2. Enter display name (config optional)
 *     3. Create -> backend mints HMAC secret automatically. Wizard
 *        shows the webhook URL + signing secret for the admin to
 *        plug into the third-party system.
 *
 * Decision 35 (LB6b-T3): step 2 also carries the classification the source's
 * synced documents take — its DEFAULT sensitivity and export control —
 * through the two shared classification selects, drawn and sent ONLY for the
 * controls the tenant's compliance frameworks show (decision 28,
 * use-classification-policy.ts). A hidden control sends no key: the source
 * keeps unclassified / none. Without a current policy answer the source is
 * not created (review LB6T7-BF-01: not knowing is not "hidden").
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  ArrowLeft, Cloud, Building2, Github, Webhook, Check, Lock,
  Loader2, ExternalLink, Shield, Copy,
  FolderArchive, HardDrive, Box, ShieldAlert,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { StepWizard } from '@/components/shared/step-wizard';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useHasPermission } from '@/lib/use-has-permission';
import {
  useCreateConnector,
  useStartOAuth,
  webhookUrlFor,
  type ConnectorKind,
  type SyncSchedule,
} from '@/hooks/useConnectors';
import { useConnectionsCapabilities } from '@/lib/use-connections-module';
import { useQueryClient } from '@tanstack/react-query';
import {
  classificationFields,
  invalidateClassificationPolicy,
  useClassificationPolicy,
  type ClassificationControlsView,
} from '@/lib/use-classification-policy';
import { ExportControlSelect, SensitivitySelect } from '@/components/library/classification-selects';
import { ClassificationPolicyError } from '@/components/library/classification-policy-error';
import { SigningSecretDialog, copyWithReport, dropSecretOnClose } from '@/components/library/signing-secret-dialog';
import { resolveConnectorDisplayName } from './-connect-helpers';

export const Route = createFileRoute('/library/sources/connect')({
  component: ConnectSourcePage,
});

const PROVIDER_META: Record<ConnectorKind, { label: string; description: string; icon: LucideIcon; color: string; bg: string }> = {
  onedrive: {
    label: 'OneDrive',
    description: 'Microsoft 365 personal or business OneDrive',
    icon: Cloud,
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-500/10',
  },
  sharepoint: {
    label: 'SharePoint',
    description: 'Microsoft 365 SharePoint document libraries',
    icon: Building2,
    color: 'text-indigo-600 dark:text-indigo-400',
    bg: 'bg-indigo-500/10',
  },
  github: {
    label: 'GitHub',
    description: 'Code, docs, READMEs from selected GitHub repos',
    icon: Github,
    color: 'text-foreground',
    bg: 'bg-foreground/10',
  },
  webhook: {
    label: 'Webhook',
    description: 'Universal HTTP endpoint for any external system',
    icon: Webhook,
    color: 'text-purple-600 dark:text-purple-400',
    bg: 'bg-purple-500/10',
  },
};

// #141 — Library UI + functional specs both call out Google Drive,
// Amazon S3, and Dropbox as supported sync surfaces. The backend
// connector pipeline doesn't have authenticators for them yet, so we
// surface them in the provider picker as disabled "Coming soon" cards
// rather than letting tenant admins assume the documented providers
// are accidentally hidden. The cards are non-selectable; clicking
// shows a tooltip / toast and leaves the wizard on the current step.
const COMING_SOON_PROVIDERS: Array<{
  key: string;
  label: string;
  description: string;
  icon: LucideIcon;
  color: string;
  bg: string;
}> = [
  {
    key: 'google_drive',
    label: 'Google Drive',
    description: 'Google Workspace personal or shared drives',
    icon: HardDrive,
    color: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-500/10',
  },
  {
    key: 's3',
    label: 'Amazon S3',
    description: 'Object storage buckets in AWS S3',
    icon: Box,
    color: 'text-orange-600 dark:text-orange-400',
    bg: 'bg-orange-500/10',
  },
  {
    key: 'dropbox',
    label: 'Dropbox',
    description: 'Dropbox personal or business accounts',
    icon: FolderArchive,
    color: 'text-sky-600 dark:text-sky-400',
    bg: 'bg-sky-500/10',
  },
];

/**
 * The default-classification keys of POST /library/connectors: ONLY the
 * controls the tenant's frameworks show (decision 28) — a hidden control
 * contributes no key, not its default. Exported for the test.
 */
export function sourceDefaultClassification(
  controls: ClassificationControlsView,
  values: { sensitivity: string; exportControl: string },
): { defaultSensitivity?: string; defaultExportControl?: string } {
  const fields = classificationFields(controls, values);
  return {
    ...(fields.sensitivity !== undefined && { defaultSensitivity: fields.sensitivity }),
    ...(fields.exportControl !== undefined && { defaultExportControl: fields.exportControl }),
  };
}

const SCHEDULES: { value: SyncSchedule; label: string }[] = [
  { value: 'every_5min', label: 'Every 5 minutes' },
  { value: 'every_15min', label: 'Every 15 minutes' },
  { value: 'every_1h', label: 'Every hour' },
  { value: 'every_6h', label: 'Every 6 hours' },
  { value: 'daily', label: 'Daily' },
  { value: 'manual_only', label: 'Manual only' },
  { value: 'webhook_only', label: 'Webhook only' },
];

function ConnectSourcePage() {
  const navigate = useNavigate();
  // B1-T3: was `useAuth().canManageSources` (roleLevel >= power_user).
  const canManageSources = useHasPermission('write', 'library');
  const create = useCreateConnector();
  const startOAuth = useStartOAuth();
  // #7 / SECC-T2 — when the tenant's "Allow external connections" setting
  // is off, the API refuses every Library connector create with a 403, so
  // the wizard must not render the provider tiles or let the user walk
  // steps that are guaranteed to fail (spec D2: the screen never offers a
  // control the submit refuses). `externalConnectionsAllowed` is the
  // setting, served by GET /connections-module/capabilities. The platform
  // posture (residual second authority until Connections C3) refuses the
  // same `external` class as `rest_api`, so a served `restApi.enabled:
  // false` hides the wizard too. The notice shows the served reason
  // verbatim — the web derives no wording of its own.
  const { data: capabilities } = useConnectionsCapabilities();
  const externalSourcesBlocked =
    capabilities?.externalConnectionsAllowed === false ||
    capabilities?.adapters.restApi.enabled === false;
  const externalSourcesBlockedReason = capabilities?.adapters.restApi.reason ?? '';

  const [kind, setKind] = useState<ConnectorKind | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [syncSchedule, setSyncSchedule] = useState<SyncSchedule>('every_15min');
  // Decision 35 — the source's default classification (shown and sent only
  // for the controls a framework shows; called before the early returns).
  const [defaultSensitivity, setDefaultSensitivity] = useState('unclassified');
  const [defaultExportControl, setDefaultExportControl] = useState('none');
  const queryClient = useQueryClient();
  const policy = useClassificationPolicy({ enabled: canManageSources && !externalSourcesBlocked });
  const { controls } = policy;

  // OneDrive / SharePoint
  const [rootFolderPath, setRootFolderPath] = useState('');
  const [siteId, setSiteId] = useState('');

  // GitHub
  const [installationId, setInstallationId] = useState('');
  const [repoList, setRepoList] = useState(''); // comma-separated owner/name pairs

  // After-create state
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null);
  // Decision 62 — the webhook source's signing secret, from the create's 201,
  // held here and nowhere else; the show-once dialog is open while it is set,
  // and closing the dialog drops it.
  const [signingSecret, setSigningSecret] = useState<string | null>(null);
  // Tracks whether the user has launched the provider OAuth flow at
  // least once. We don't redirect back through TanStack here -- the
  // provider redirects to /api/v1/library/connectors/:id/oauth-callback
  // and the connector becomes hasCredentials=true server-side. This
  // flag gates the wizard's "Done" button so users can't dismiss the
  // wizard before they've at least started OAuth (UI audit 2026-04-29
  // HIGH #1).
  const [oauthLaunched, setOauthLaunched] = useState(false);

  if (!canManageSources) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <Shield className="h-12 w-12 text-muted-foreground/20 mb-3" />
        <h2 className="text-lg font-semibold">Admin Access Required</h2>
        <Button variant="outline" className="mt-4" onClick={() => navigate({ to: '/library/sources' })}>
          Back to Sources
        </Button>
      </div>
    );
  }

  // #7 — Replace the entire wizard with the refusal notice so no
  // disallowed provider tile renders and the user can't walk steps that
  // are guaranteed to 403 on the final POST.
  if (externalSourcesBlocked) {
    return (
      <div className="flex flex-col h-full">
        <div className="border-b border-border/40 bg-background px-6 py-3 flex items-center gap-3 shrink-0">
          <Button variant="ghost" size="icon" className="h-8 w-8" title="Back to Sources" onClick={() => navigate({ to: '/library/sources' })}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Separator orientation="vertical" className="h-5" />
          <div>
            <h1 className="text-lg font-semibold">Connect external source</h1>
            <p className="text-xs text-muted-foreground">Sync documents from cloud storage, code repos, or any HTTP endpoint</p>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-6">
          <Card className="max-w-2xl mx-auto border-amber-500/30 bg-amber-500/5">
            <CardContent className="pt-6 pb-5 flex flex-col items-center text-center gap-3">
              <ShieldAlert className="h-10 w-10 text-amber-600" />
              <h2 className="text-base font-semibold">External sources are turned off</h2>
              <p className="text-sm text-muted-foreground max-w-lg" data-testid="external-sources-blocked-reason">
                {externalSourcesBlockedReason}
              </p>
              <p className="text-sm text-muted-foreground max-w-lg">
                To ingest documents, upload them directly from the{' '}
                <span className="font-medium">Documents</span> tab instead.
              </p>
              <Button variant="outline" className="mt-2" onClick={() => navigate({ to: '/library/sources' })}>
                Back to Sources
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const handleCreate = async () => {
    if (!kind) return;
    // Not knowing the tenant's classification controls is not "hidden"
    // (review LB6T7-BF-01): no current answer, no create.
    if (!policy.ready) return;
    // #7 — defence-in-depth: even if the refusal notice were bypassed
    // (stale capabilities), never fire the create — the backend 403s
    // anyway, but surface the served reason rather than a silent failure.
    if (externalSourcesBlocked) {
      toast.error(externalSourcesBlockedReason);
      return;
    }
    let config: Record<string, unknown> = {};
    if (kind === 'onedrive') {
      if (rootFolderPath) config.rootFolderPath = rootFolderPath;
    } else if (kind === 'sharepoint') {
      if (!siteId.trim()) {
        toast.error('siteId required for SharePoint');
        return;
      }
      config.siteId = siteId.trim();
      if (rootFolderPath) config.rootFolderPath = rootFolderPath;
    } else if (kind === 'github') {
      if (!installationId.trim()) {
        toast.error('GitHub installation ID required');
        return;
      }
      const repos = repoList
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => {
          const [owner, name] = s.split('/');
          return { owner: owner ?? '', name: name ?? '' };
        });
      if (repos.length === 0) {
        toast.error('At least one owner/repo required');
        return;
      }
      config.installationId = installationId.trim();
      config.selectedRepos = repos;
    }

    // #699 — never silently substitute a provider-label fallback. The
    // wizard's `canProceed()` already enforces a non-empty trimmed
    // display name; this resolver makes the contract explicit and
    // fails loudly if someone bypasses the gate.
    const resolvedName = resolveConnectorDisplayName(displayName);
    if (resolvedName == null) {
      toast.error('Display name is required.');
      return;
    }

    try {
      const created = await create.mutateAsync({
        kind,
        displayName: resolvedName,
        syncSchedule,
        syncEnabled: kind !== 'webhook' ? true : false, // webhooks default to enabled too; explicit
        config,
        ...sourceDefaultClassification(controls, { sensitivity: defaultSensitivity, exportControl: defaultExportControl }),
      });
      setCreatedId(created.id);
      if (kind === 'webhook') {
        setWebhookUrl(webhookUrlFor(created.id));
        setSigningSecret(created.signingSecret ?? null);
        toast.success('Webhook connector created');
      } else {
        toast.success(`Connector created. Authenticate to start syncing.`);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Create failed');
      // A framework turned on or off since the wizard opened: re-ask (review
      // LB6T7-BF-06).
      void invalidateClassificationPolicy(queryClient);
    }
  };

  const handleAuthenticate = async () => {
    if (!createdId) return;
    try {
      const result = await startOAuth.mutateAsync(createdId);
      // #138 — Same placeholder guard the source-card path already
      // applies (see sources/index.tsx). When the deployment hasn't
      // configured provider OAuth credentials the backend returns an
      // authUrl containing `_PLACEHOLDER` substring or an empty
      // client_id. Redirecting would dump the user on a Microsoft /
      // Google error page with no context. Show the same in-app
      // "OAuth is not configured" toast and keep them in the wizard
      // so they can ask sysadmin to fix the env var.
      if (!result.authUrl || /_PLACEHOLDER|client_id=$|client_id=&/i.test(result.authUrl)) {
        const providerLabel = kind ? PROVIDER_META[kind].label : 'OAuth';
        toast.error(
          `${providerLabel} OAuth is not configured on this deployment. Ask your sysadmin to set the provider client_id and redirect_uri before connecting.`,
        );
        return;
      }
      // Mark that OAuth has been launched so the "Done" button on the
      // last wizard step becomes enabled. The actual completion is
      // handled by the provider redirecting back into the connector's
      // oauth-callback endpoint -- by that point the user has left
      // the wizard already, so the button needs to be enabled BEFORE
      // we navigate away.
      setOauthLaunched(true);
      window.location.href = result.authUrl;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'OAuth start failed');
    }
  };

  const steps = [
    {
      id: 'provider',
      title: 'Choose provider',
      description: 'Pick which external system you want to sync from',
      canProceed: () => kind !== null,
      content: (
        <div className="grid grid-cols-1 gap-3 max-w-xl">
          {(Object.entries(PROVIDER_META) as [ConnectorKind, typeof PROVIDER_META[ConnectorKind]][]).map(([key, meta]) => {
            const Icon = meta.icon;
            const selected = kind === key;
            return (
              <Card
                key={key}
                className={cn(
                  'cursor-pointer transition-colors',
                  selected ? 'border-primary ring-1 ring-primary/20' : 'hover:border-primary/30',
                )}
                onClick={() => {
                  setKind(key);
                  if (!displayName) setDisplayName(meta.label);
                }}
              >
                <CardContent className="pt-4 pb-3 flex items-start gap-4">
                  <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center shrink-0', meta.bg)}>
                    <Icon className={cn('h-5 w-5', meta.color)} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{meta.label}</span>
                      {selected && <Check className="h-4 w-4 text-primary" />}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{meta.description}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {/* #141 — Documented providers that the connector pipeline
              doesn't authenticate yet. Disabled cards so tenant admins
              know the providers are coming, not accidentally hidden. */}
          {COMING_SOON_PROVIDERS.map((meta) => {
            const Icon = meta.icon;
            return (
              <Card
                key={meta.key}
                className="opacity-60 cursor-not-allowed"
                aria-disabled="true"
                title={`${meta.label} support is on the roadmap`}
              >
                <CardContent className="pt-4 pb-3 flex items-start gap-4">
                  <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center shrink-0', meta.bg)}>
                    <Icon className={cn('h-5 w-5', meta.color)} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{meta.label}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground uppercase tracking-wide">
                        Coming soon
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{meta.description}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ),
    },
    {
      id: 'config',
      title: 'Configure',
      description: 'Name the connector and set provider-specific options',
      // #9 — also enforce the kind-specific required fields so the Next
      // button stays disabled (with the wizard's 'complete the required
      // fields' tooltip) until SharePoint siteId / GitHub installationId +
      // at least one valid owner/repo are filled. Previously canProceed
      // only checked displayName, so the wizard advanced to Step 3 with
      // empty required inputs and only surfaced the error at create time.
      canProceed: () => {
        if (kind === null || displayName.trim().length === 0) return false;
        if (kind === 'sharepoint') return siteId.trim().length > 0;
        if (kind === 'github') {
          const hasRepo = repoList
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
            .some((s) => {
              const [owner, name] = s.split('/');
              return Boolean(owner) && Boolean(name);
            });
          return installationId.trim().length > 0 && hasRepo;
        }
        return true;
      },
      content: (
        <div className="space-y-3 max-w-md">
          <div className="space-y-1.5">
            <Label className="text-xs">Display name</Label>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="h-8 text-xs"
              placeholder="e.g., OneDrive — Engineering"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Sync schedule</Label>
            <Select value={syncSchedule} onValueChange={(v) => setSyncSchedule(v as SyncSchedule)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SCHEDULES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {/* Decision 35 — what the source's synced documents are classified as;
              only the controls a framework shows (decision 28). */}
          {controls.any ? (
            <div className="rounded-md border border-muted p-3 space-y-3" data-testid="source-default-classification">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Classification of synced documents</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">Every document this source syncs is stored with this classification.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {controls.sensitivity ? <SensitivitySelect value={defaultSensitivity} onChange={setDefaultSensitivity} /> : null}
                {controls.exportControl ? <ExportControlSelect value={defaultExportControl} onChange={setDefaultExportControl} /> : null}
              </div>
            </div>
          ) : null}
          {policy.unavailable ? <ClassificationPolicyError onRetry={policy.retry} /> : null}
          {kind === 'onedrive' && (
            <div className="space-y-1.5">
              <Label className="text-xs">Root folder path (optional)</Label>
              <Input
                value={rootFolderPath}
                onChange={(e) => setRootFolderPath(e.target.value)}
                className="h-8 text-xs font-mono"
                placeholder="/Documents/Engineering"
              />
            </div>
          )}
          {kind === 'sharepoint' && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">Site ID<span className="text-red-500 ml-0.5">*</span></Label>
                <Input
                  value={siteId}
                  onChange={(e) => setSiteId(e.target.value)}
                  className="h-8 text-xs font-mono"
                  placeholder="contoso.sharepoint.com,..."
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Root folder path (optional)</Label>
                <Input
                  value={rootFolderPath}
                  onChange={(e) => setRootFolderPath(e.target.value)}
                  className="h-8 text-xs font-mono"
                  placeholder="/Shared Documents"
                />
              </div>
            </>
          )}
          {kind === 'github' && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">Installation ID<span className="text-red-500 ml-0.5">*</span></Label>
                <Input
                  value={installationId}
                  onChange={(e) => setInstallationId(e.target.value)}
                  className="h-8 text-xs font-mono"
                  placeholder="12345678"
                />
                <p className="text-[10px] text-muted-foreground">
                  Numeric ID from your GitHub App installation. Find it under Settings -&gt; Apps.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Repositories<span className="text-red-500 ml-0.5">*</span></Label>
                <Input
                  value={repoList}
                  onChange={(e) => setRepoList(e.target.value)}
                  className="h-8 text-xs font-mono"
                  placeholder="myorg/repo1, myorg/repo2"
                />
                <p className="text-[10px] text-muted-foreground">
                  Comma-separated owner/repo pairs. After authenticating, you can refine this list.
                </p>
              </div>
            </>
          )}
          {kind === 'webhook' && (
            <Card className="bg-muted/40">
              <CardContent className="pt-3 pb-2">
                <p className="text-xs text-muted-foreground">
                  Webhook connectors don&apos;t need additional config. After creation, you&apos;ll get a URL and signing secret to plug into your external system.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      ),
    },
    {
      id: 'create',
      title: 'Create + authenticate',
      description: 'Save the connector and complete authentication',
      // The connector must exist AND the kind-specific authentication
      // step must have run. For OAuth kinds (onedrive/sharepoint/github)
      // we require the user to have at least launched the provider OAuth
      // flow so the connector is on its way to hasCredentials=true. For
      // webhook we require the signing URL to have been generated and
      // (implicitly) shown to the admin so they can wire it into the
      // upstream system. This prevents the user from dismissing the
      // wizard with an unauthenticated connector (UI audit 2026-04-29
      // HIGH #1).
      canProceed: () => {
        if (createdId === null || kind === null) return false;
        if (kind === 'webhook') return webhookUrl !== null;
        return oauthLaunched;
      },
      content: createdId ? (
        <SuccessPanel
          kind={kind!}
          connectorId={createdId}
          webhookUrl={webhookUrl}
          onAuthenticate={handleAuthenticate}
          authPending={startOAuth.isPending}
          oauthLaunched={oauthLaunched}
        />
      ) : (
        <Card className="max-w-md border-dashed">
          <CardContent className="pt-5 pb-4 flex flex-col items-center text-center">
            <Lock className="h-8 w-8 text-muted-foreground/30 mb-3" />
            <p className="text-sm font-medium mb-1">Ready to create</p>
            <p className="text-xs text-muted-foreground mb-3">
              We&apos;ll save the connector. {kind !== 'webhook' ? 'Then you\'ll authenticate with the provider.' : 'No authentication required for webhooks.'}
            </p>
            <Button onClick={handleCreate} disabled={create.isPending || !policy.ready} className="gap-1.5">
              {create.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              Create connector
            </Button>
          </CardContent>
        </Card>
      ),
    },
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-border/40 bg-background px-6 py-3 flex items-center gap-3 shrink-0">
        <Button variant="ghost" size="icon" className="h-8 w-8" title="Back to Sources" onClick={() => navigate({ to: '/library/sources' })}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <Separator orientation="vertical" className="h-5" />
        <div>
          <h1 className="text-lg font-semibold">Connect external source</h1>
          <p className="text-xs text-muted-foreground">Sync documents from cloud storage, code repos, or any HTTP endpoint</p>
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <StepWizard
          steps={steps}
          onComplete={() => navigate({ to: '/library/sources' })}
          onCancel={() => navigate({ to: '/library/sources' })}
          completeLabel="Done"
          className="max-w-3xl mx-auto p-6"
        />
      </div>
      {signingSecret !== null && webhookUrl !== null && (
        <SigningSecretDialog
          open
          onOpenChange={dropSecretOnClose(() => setSigningSecret(null), create.reset)}
          secret={signingSecret}
          webhookUrl={webhookUrl}
          context="created"
        />
      )}
    </div>
  );
}

function SuccessPanel({
  kind, connectorId, webhookUrl, onAuthenticate, authPending, oauthLaunched,
}: {
  kind: ConnectorKind;
  connectorId: string;
  webhookUrl: string | null;
  onAuthenticate: () => void;
  authPending: boolean;
  oauthLaunched: boolean;
}) {
  if (kind === 'webhook' && webhookUrl) {
    return (
      <Card className="max-w-xl border-emerald-500/30 bg-emerald-500/5">
        <CardContent className="pt-4 pb-3 space-y-3">
          <div className="flex items-center gap-2">
            <Check className="h-4 w-4 text-emerald-600" />
            <p className="text-sm font-medium">Webhook ready</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Webhook URL</Label>
            <div className="flex gap-2">
              <Input value={webhookUrl} readOnly className="h-8 text-xs font-mono" />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void copyWithReport(webhookUrl, 'url')}
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Configure your external system to POST to this URL. Sign each POST with HMAC-SHA256 over the raw body
            (<code className="text-[10px] px-1 bg-muted rounded">X-Argus-Signature: sha256=&lt;hex&gt;</code>) using
            the signing secret shown when this source was created. Lost it? Rotate it from the source&apos;s Settings.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-md">
      <CardContent className="pt-4 pb-3 space-y-3">
        <div className="flex items-center gap-2">
          <Check className="h-4 w-4 text-emerald-600" />
          <p className="text-sm font-medium">Connector created</p>
        </div>
        <p className="text-xs text-muted-foreground">
          ID: <code className="text-[10px] px-1 bg-muted rounded">{connectorId}</code>
        </p>
        {oauthLaunched ? (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5 text-xs text-amber-800 dark:text-amber-200">
            OAuth started. After signing in with the provider you will land
            back on the Sources page. If you cancel before completing, the
            connector will exist but won&apos;t sync until you authenticate.
            You can re-launch authentication from the connector detail page.
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Click below to authenticate with the provider. You will be
            redirected to their login page. The <span className="font-medium">Done</span>
            {' '}button stays disabled until OAuth has been launched.
          </p>
        )}
        <Button onClick={onAuthenticate} disabled={authPending} className="gap-1.5">
          {authPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3" />}
          {oauthLaunched ? 'Re-launch authentication' : 'Authenticate now'}
        </Button>
      </CardContent>
    </Card>
  );
}
